/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Subscriptions API
 * What a practice pays **Scrivio** — its plan, its seats, its periods and its discounts.  This is the opposite direction of money from `openapi-billing.yaml` and `openapi-invoices.yaml`, where an invoice is something the practice sends to a cliënt or declares to an insurer. The two share the English word \"invoice\" and almost nothing else, which is why subscription billing is a bounded context of its own (ADR-042) and why every schema here is prefixed `Subscription…`: all specs generate into one Java package, so `InvoiceResponse` and `InvoiceStatus` are names that are already taken.  ## Authentication All endpoints require a Firebase Authentication JWT token in the `Authorization: Bearer <token>` header, and answer for the caller\'s active organisation only — there is no path parameter naming a subscription.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * How Scrivio collects from the practice. `INVOICE` is a bank transfer against an invoice; `AUTOMATED` is direct debit against a signed mandate.  Deliberately not called `BillingMethod`: that already means how a *practice* declares care to an insurer, which is the other direction of money entirely. 
 */

export const SubscriptionBillingMode = {
    Invoice: 'INVOICE',
    Automated: 'AUTOMATED'
} as const;

export type SubscriptionBillingMode = typeof SubscriptionBillingMode[keyof typeof SubscriptionBillingMode];


/**
 * How the practice wants Scrivio to collect.
 */
export interface SubscriptionBillingModeRequest {
    'billingMode': SubscriptionBillingMode;
}


/**
 * How a subscription ends. `IMMEDIATE` ends it today — what a practice in its free trial gets. `WITH_NOTICE` gives a month\'s notice to the end of a calendar month (agreement art. 9.1), billed through its end. 
 */

export const SubscriptionCancellationMode = {
    Immediate: 'IMMEDIATE',
    WithNotice: 'WITH_NOTICE'
} as const;

export type SubscriptionCancellationMode = typeof SubscriptionCancellationMode[keyof typeof SubscriptionCancellationMode];


/**
 * What `POST /api/v1/subscription/cancellation` would do if sent today, so the practice is shown the end date before it confirms. Present exactly when that request is accepted. 
 */
export interface SubscriptionCancellationOffer {
    'mode': SubscriptionCancellationMode;
    /**
     * The subscription\'s last day: today for `IMMEDIATE`, the end of the notice period for `WITH_NOTICE`. 
     */
    'endsOn': string;
}


/**
 * Where to send the practice: Mollie\'s hosted checkout, for a mandate or — from `POST /api/v1/subscription/invoices/{invoiceId}/checkout` — for one invoice. 
 */
export interface SubscriptionCheckoutResponse {
    /**
     * Mollie\'s hosted checkout. Short-lived at Mollie, so it is followed at once rather than stored; a later attempt asks for a new one. 
     */
    'checkoutUrl': string;
}
/**
 * Why an `AUTOMATED` subscription is collected as `INVOICE`. `NO_MANDATE` — the practice has not signed one yet; `MANDATE_PENDING` — a SEPA mandate is signed and awaiting its first collection; `MANDATE_REVOKED` — the mandate was usable and has been withdrawn; `MANDATE_INVALID` — Mollie refused it, so it was never usable. 
 */

export const SubscriptionCollectionFallbackReason = {
    NoMandate: 'NO_MANDATE',
    MandatePending: 'MANDATE_PENDING',
    MandateRevoked: 'MANDATE_REVOKED',
    MandateInvalid: 'MANDATE_INVALID'
} as const;

export type SubscriptionCollectionFallbackReason = typeof SubscriptionCollectionFallbackReason[keyof typeof SubscriptionCollectionFallbackReason];


/**
 * A dated percentage off this subscription.
 */
export interface SubscriptionDiscountResponse {
    /**
     * Identifier of the discount.
     */
    'id': string;
    /**
     * 0..100, two decimals. 100 is a free period.
     */
    'percentage': number;
    /**
     * First day the discount applies. Inclusive.
     */
    'startsOn': string;
    /**
     * Last day the discount applies, inclusive. Absent means open-ended, which is what a negotiated discount on a signed contract usually is. 
     */
    'endsOn'?: string;
    /**
     * What the invoice calls it.
     */
    'label': string;
}
/**
 * An invoice Scrivio sent the practice, as the practice sees it.
 */
export interface SubscriptionInvoiceResponse {
    'id': string;
    'invoiceNumber': string;
    'invoiceDate': string;
    'dueDate': string;
    /**
     * What the invoice calls the period, e.g. \"oktober 2026\".
     */
    'periodLabel': string;
    /**
     * Including BTW. Negative on a credit note.
     */
    'totalEuro': number;
    'status': SubscriptionInvoiceStatus;
    'billingModeAtIssue': SubscriptionBillingMode;
    /**
     * Whether this is a credit note correcting an earlier invoice.
     */
    'creditNote': boolean;
    /**
     * Whether `GET /api/v1/subscription/invoices/{invoiceId}/pdf` has a document to serve. `false` for an invoice that covers no period, which is not rendered as a PDF here. 
     */
    'hasPdf': boolean;
    /**
     * Whether `POST /api/v1/subscription/invoices/{invoiceId}/checkout` is offered for this invoice — the \"Betalen\" link. `true` for an invoice the practice still owes by bank transfer (`billingModeAtIssue` `INVOICE`, `CONCEPT` or `VERSTUURD`, more than €0,00), where Scrivio has online payment switched on. A direct-debit invoice is Mollie\'s to collect and is never `true`. 
     */
    'payableOnline': boolean;
}


/**
 * Kept in Dutch, the vocabulary of Scrivio\'s own debtor overview.
 */

export const SubscriptionInvoiceStatus = {
    Concept: 'CONCEPT',
    Verstuurd: 'VERSTUURD',
    Betaald: 'BETAALD',
    Oninbaar: 'ONINBAAR'
} as const;

export type SubscriptionInvoiceStatus = typeof SubscriptionInvoiceStatus[keyof typeof SubscriptionInvoiceStatus];


/**
 * What the next invoice will come to if nothing changes before it is issued — the period it covers, computed by the same arithmetic that will bill it, with today\'s behandelaars standing in for the ones present on its first day.  A projection, not a promise: a behandelaar added or removed before that day moves it, and nothing during the period does (#4460).  Absent where no invoice is coming: a subscription Scrivio collects nothing from, one that stops at the end of the running period, a period that comes to nothing — a free trial covering all of it, most commonly — because a zero period is closed without an invoice, and a practice that is suspended, which is not invoiced while it is (#4897). 
 */
export interface SubscriptionNextInvoiceResponse {
    /**
     * First day of the period it covers. Inclusive.
     */
    'periodStart': string;
    /**
     * Last day of the period it covers. Inclusive.
     */
    'periodEnd': string;
    /**
     * The day it is expected to be issued: the period\'s first day, or today for a period that has already begun and has not been billed yet. 
     */
    'issuedOn': string;
    /**
     * Excluding BTW, discounts already taken off.
     */
    'subtotalEuro': number;
    'btwEuro': number;
    /**
     * What the practice will be asked to pay.
     */
    'totalEuro': number;
}
/**
 * One organisation\'s subscription to Scrivio.  Everything beyond `status`, `mandateRequired`, `seatsToday` and the two collections is optional, because an `UNMANAGED` practice has none of it: no plan, no period, no agreed seat price. 
 */
export interface SubscriptionResponse {
    'status': SubscriptionStatus;
    /**
     * What the plan is called, e.g. \"Scrivio Volledig\". Absent while `UNMANAGED` — every other status means money is being computed, and that needs a plan. 
     */
    'planName'?: string;
    'billingMode'?: SubscriptionBillingMode;
    /**
     * How the next amount will actually be collected. The same as `billingMode`, except that `AUTOMATED` without a chargeable mandate is collected as `INVOICE` — the state a practice is in between choosing direct debit and signing for it (\"automatisch, nog geen machtiging\"). Present whenever `billingMode` is. 
     */
    'collectedAs'?: SubscriptionBillingMode;
    /**
     * Why `collectedAs` is `INVOICE` while `billingMode` is `AUTOMATED`, read off the latest mandate. Present exactly then, and absent whenever the chosen mode is the one collected. 
     */
    'collectionFallbackReason'?: SubscriptionCollectionFallbackReason;
    'nextInvoice'?: SubscriptionNextInvoiceResponse;
    /**
     * First day of the period being billed. Inclusive. Absent before activation.
     */
    'currentPeriodStart'?: string;
    /**
     * Last day of the period being billed. **Inclusive** — a September period ends 2026-09-30, not 2026-10-01. Absent before activation. 
     */
    'currentPeriodEnd'?: string;
    /**
     * First day of the period after the running one — the day after `currentPeriodEnd`. Absent exactly where `currentPeriodEnd` is.  It is here because it is the day a **seat change made today first reaches an invoice**: the seats a period is billed for are the ones present on its first day, and nothing during a period changes that period\'s bill (#4460). A client naming that day must read it from here rather than compute \"the first of next month\", which differs for a subscription whose first period started partway through a month. 
     */
    'nextPeriodStart'?: string;
    /**
     * Whether the practice has given notice. True exactly when `endsOn` is present. 
     */
    'cancelAtPeriodEnd'?: boolean;
    /**
     * The subscription\'s last day once notice is given, inclusive — the end of the notice period, billed through and never after. Present exactly while `cancelAtPeriodEnd` is `true`; until that day the notice can be withdrawn. 
     */
    'endsOn'?: string;
    'cancellationOffer'?: SubscriptionCancellationOffer;
    /**
     * Last day of the running free trial, inclusive. Present only while one is actually running. A trial is a 100% discount dated from signup rather than a status of its own, so this is that discount\'s end date. 
     */
    'trialEndsOn'?: string;
    /**
     * Whether the practice still has to sign a direct-debit mandate: it is `ACTIVE`, chose `AUTOMATED`, and has no valid mandate. Exactly when `POST /api/v1/subscription/mandate-checkout` is accepted. `false` for every other practice, an `UNMANAGED` one included. 
     */
    'mandateRequired': boolean;
    /**
     * The day the mandate is needed by — the last day of the running trial, inclusive, after which Scrivio starts charging. Present only while `mandateRequired` is `true` and a trial is running; `mandateRequired` without it means the trial is over and the practice is invoiced until it signs. 
     */
    'mandateRequiredBy'?: string;
    /**
     * The masked account of the mandate an `AUTOMATED` amount is collected under now — the newest valid one — as Mollie states it, so a praktijkhouder can tell which account they authorised. Never a full account number. Present exactly when the subscription is `ACTIVE`, `AUTOMATED` and has a valid mandate, which is also exactly when `POST /api/v1/subscription/mandate-replacement-checkout` is accepted. 
     */
    'mandateDisplayLabel'?: string;
    /**
     * How many seats the practice occupies right now: its enabled behandelaar memberships. Counted after the fact and priced — never a quota, and never a reason to refuse a new behandelaar (ADR-042 §3). 
     */
    'seatsToday': number;
    /**
     * What **this** subscription pays per seat, as agreed when it was activated. Not the plan\'s price today: raising a plan\'s price must not silently reprice a practice already on it. Empty while `UNMANAGED`. 
     */
    'seatPrices': Array<SubscriptionSeatPriceResponse>;
    /**
     * The discounts that still count: granted, not revoked, and not yet ended. A discount that has not started yet is included — it is an arrangement the practice has been given, and its `startsOn` says when it begins. 
     */
    'activeDiscounts': Array<SubscriptionDiscountResponse>;
}


/**
 * The agreed price of one kind of seat on this subscription.
 */
export interface SubscriptionSeatPriceResponse {
    'seatType': SubscriptionSeatType;
    /**
     * Euros per seat per billing interval, excluding BTW. Zero is a price, not a gap.
     */
    'pricePerSeatEuro': number;
}


/**
 * What kind of seat is being priced. A `BEHANDELAAR` seat is an enabled membership carrying the behandelaar designation. 
 */

export const SubscriptionSeatType = {
    Behandelaar: 'BEHANDELAAR'
} as const;

export type SubscriptionSeatType = typeof SubscriptionSeatType[keyof typeof SubscriptionSeatType];


/**
 * Where the subscription stands.  There is no `TRIALING`: a free trial is a 100% discount dated from signup, so that \"trial\" and \"discount\" stay one mechanism instead of two that have to agree. 
 */

export const SubscriptionStatus = {
    Unmanaged: 'UNMANAGED',
    Active: 'ACTIVE',
    PastDue: 'PAST_DUE',
    Unpaid: 'UNPAID',
    Canceled: 'CANCELED'
} as const;

export type SubscriptionStatus = typeof SubscriptionStatus[keyof typeof SubscriptionStatus];



/**
 * SubscriptionsApi - axios parameter creator
 */
export const SubscriptionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Ends the subscription of the caller\'s own organisation on the terms its `cancellationOffer` states, which the screen shows before it asks (#4902):  - **During the free trial it ends now.** The subscription is `CANCELED`, every mandate is   withdrawn and the organisation becomes read-only. Nothing is ever invoiced: the free   period covers every day up to today and no later period is billed (agreement art. 4.2). - **Afterwards notice is given** (art. 9.1): one month, to the end of a calendar month.   The subscription stays `ACTIVE` and is billed through `endsOn`, and never after it. The   notice can be withdrawn until then with `DELETE`.  The practice is mailed a confirmation and Scrivio is told. Accepted while the organisation is read-only, for non-payment or a suspension alike: a practice that cannot write must still be able to leave.  Refused with `409` where there is no `cancellationOffer`: an `UNMANAGED` subscription, which Scrivio does not collect, one already `CANCELED`, and one with notice already running. 
         * @summary End the practice\'s own subscription (\"Abonnement opzeggen\")
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelMySubscription: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/subscription/cancellation`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a Mollie **one-off** payment for the invoice\'s total and returns the hosted checkout to send the practice to — the \"Betalen\" link. Once Mollie reports it paid, the invoice is `BETAALD` through Mollie\'s notification, with nobody marking it by hand. A plain bank transfer remains possible and is still recorded by Scrivio.  The payment is not a direct debit: no mandate is given or used, and the invoice keeps `billingModeAtIssue` `INVOICE`. Every call creates a new payment, because a checkout URL is short-lived at Mollie; after it — paid or abandoned — Mollie returns the practice to the app, whose read of `GET /api/v1/subscription` asks Mollie about a checkout still open.  Accepted exactly where the invoice reads `payableOnline: true`, and refused with `409` while another payment of the invoice is pending or paid, so one invoice is not paid twice. 
         * @summary Start a Mollie checkout to pay one invoice the practice owes by transfer
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMySubscriptionInvoiceCheckout: async (invoiceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('createMySubscriptionInvoiceCheckout', 'invoiceId', invoiceId)
            const localVarPath = `/api/v1/subscription/invoices/{invoiceId}/checkout`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a Mollie **first payment** (`sequenceType: first`) for the caller\'s own organisation and returns the hosted checkout to send the practice to. Completing it is what gives Scrivio a mandate to collect `AUTOMATED` payments under.  The payment is a **verification amount** — Mollie\'s minimum, €0,01. It pays for no period and is on no invoice; it exists to establish the mandate. It is asked for before the trial ends rather than at signup, so a payment page never stands in front of the free trial.  After the checkout — completed or abandoned — Mollie returns the practice to the app, which reads `GET /api/v1/subscription`: `mandateRequired` then says whether the mandate was given. That read also asks Mollie about a checkout that is still open, because Mollie\'s notification can arrive after the practice does.  Accepted only while `mandateRequired` would be `true`: an `ACTIVE` subscription whose `billingMode` is `AUTOMATED` and which has no valid mandate yet. Anything else is `409` — an `UNMANAGED` or overdue subscription, a practice paying by `INVOICE`, and one that has already signed. 
         * @summary Start the checkout that gives Scrivio a direct-debit mandate
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMySubscriptionMandateCheckout: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/subscription/mandate-checkout`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The same €0,01 first payment as `POST /api/v1/subscription/mandate-checkout`, on the same Mollie customer, for a practice that **already** has a valid mandate and wants to be collected from a different account — a new bank account, or a mandate set up wrongly.  **The current mandate stays in force until the new one is valid.** It is revoked — at Mollie and here, audited — only when the mandate this checkout produces arrives as valid, so there is never a moment without one: an invoice collected while the checkout is open is collected under the current mandate, and one collected afterwards under the new. A checkout that is abandoned or fails changes nothing.  Accepted only for an `ACTIVE` subscription whose `billingMode` is `AUTOMATED` and which has a valid mandate — exactly when `mandateDisplayLabel` is present on `GET /api/v1/subscription`. Without one, `POST /api/v1/subscription/mandate-checkout` is the step to take, and this is `409`. 
         * @summary Start the checkout that authorises a different account while the mandate stays in force
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMySubscriptionMandateReplacementCheckout: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/subscription/mandate-replacement-checkout`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The stored PDF, which is the one document of record: the invoice mail attaches the same object, so what the practice downloads is byte for byte what it is or was sent. It is rendered when the invoice is issued; an invoice whose render failed then is rendered and stored on its first download, and served from storage from then on.  `404` for an invoice of another organisation, and for one with no PDF — an invoice that covers no period, which `hasPdf` already reports as `false`. 
         * @summary The PDF of one invoice Scrivio issued the practice
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadMySubscriptionInvoicePdf: async (invoiceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('downloadMySubscriptionInvoicePdf', 'invoiceId', invoiceId)
            const localVarPath = `/api/v1/subscription/invoices/{invoiceId}/pdf`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/pdf,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the subscription of the caller\'s active organisation: where it stands, what it is on, what it pays per seat, how many seats it has today, and which discounts are running.  **There is no 404.** A practice that Scrivio invoices by hand is `UNMANAGED`, and that is an answer rather than an absence — it is the status every organisation that predates this context carries. An organisation whose subscription has not been recorded yet is reported as `UNMANAGED` for the same reason: it is invoiced outside Scrivio, which is exactly what the status means. The fields that only a managed subscription has — `planName`, `billingMode`, the current period — are then simply absent. 
         * @summary The caller\'s own subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMySubscription: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/subscription`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every invoice Scrivio has **issued** the caller\'s active organisation, newest first, credit notes included — and an invoice not mailed yet (`CONCEPT`) as well. A practice without a billing email is never mailed its invoices, and this list is then the only place it sees them.  An organisation Scrivio invoices by hand has no invoices here — those are sent outside Scrivio — so an `UNMANAGED` practice is answered with an empty list rather than a 404. 
         * @summary The invoices Scrivio issued the caller\'s own practice
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMySubscriptionInvoices: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/subscription/invoices`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records how the caller\'s own organisation wants Scrivio to collect: `INVOICE` (an emailed invoice, paid by bank transfer) or `AUTOMATED` (off-session direct debit or card against a Mollie mandate). Both share one ledger; only collection differs.  **`AUTOMATED` is stored at once and collected as `INVOICE` until a mandate exists.** Choosing it signs nothing: the mandate is given at Mollie\'s checkout, a separate step. The response\'s `collectedAs` says which of the two the next amount will actually be collected by, so the answer to this request already tells the practice that it is still waiting.  Refused with `409` for an `UNMANAGED` subscription — a practice Scrivio invoices by hand is not on a plan and has nothing to choose — and for a `CANCELED` one, which has left. Choosing the mode it already has changes nothing and is answered `200`. 
         * @summary Choose how the practice pays
         * @param {SubscriptionBillingModeRequest} subscriptionBillingModeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMySubscriptionBillingMode: async (subscriptionBillingModeRequest: SubscriptionBillingModeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'subscriptionBillingModeRequest' is not null or undefined
            assertParamExists('updateMySubscriptionBillingMode', 'subscriptionBillingModeRequest', subscriptionBillingModeRequest)
            const localVarPath = `/api/v1/subscription/billing-mode`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(subscriptionBillingModeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Withdraws a notice still running (#4902): the subscription carries on as before, billed as before, and `endsOn` is gone. Accepted up to and including `endsOn`; the day after, the subscription has ended. Accepted while the organisation is read-only, like the notice.  Refused with `409` when no notice runs. 
         * @summary Withdraw the notice the practice gave
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        withdrawMySubscriptionCancellation: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/subscription/cancellation`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SubscriptionsApi - functional programming interface
 */
export const SubscriptionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SubscriptionsApiAxiosParamCreator(configuration)
    return {
        /**
         * Ends the subscription of the caller\'s own organisation on the terms its `cancellationOffer` states, which the screen shows before it asks (#4902):  - **During the free trial it ends now.** The subscription is `CANCELED`, every mandate is   withdrawn and the organisation becomes read-only. Nothing is ever invoiced: the free   period covers every day up to today and no later period is billed (agreement art. 4.2). - **Afterwards notice is given** (art. 9.1): one month, to the end of a calendar month.   The subscription stays `ACTIVE` and is billed through `endsOn`, and never after it. The   notice can be withdrawn until then with `DELETE`.  The practice is mailed a confirmation and Scrivio is told. Accepted while the organisation is read-only, for non-payment or a suspension alike: a practice that cannot write must still be able to leave.  Refused with `409` where there is no `cancellationOffer`: an `UNMANAGED` subscription, which Scrivio does not collect, one already `CANCELED`, and one with notice already running. 
         * @summary End the practice\'s own subscription (\"Abonnement opzeggen\")
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async cancelMySubscription(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.cancelMySubscription(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.cancelMySubscription']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a Mollie **one-off** payment for the invoice\'s total and returns the hosted checkout to send the practice to — the \"Betalen\" link. Once Mollie reports it paid, the invoice is `BETAALD` through Mollie\'s notification, with nobody marking it by hand. A plain bank transfer remains possible and is still recorded by Scrivio.  The payment is not a direct debit: no mandate is given or used, and the invoice keeps `billingModeAtIssue` `INVOICE`. Every call creates a new payment, because a checkout URL is short-lived at Mollie; after it — paid or abandoned — Mollie returns the practice to the app, whose read of `GET /api/v1/subscription` asks Mollie about a checkout still open.  Accepted exactly where the invoice reads `payableOnline: true`, and refused with `409` while another payment of the invoice is pending or paid, so one invoice is not paid twice. 
         * @summary Start a Mollie checkout to pay one invoice the practice owes by transfer
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMySubscriptionInvoiceCheckout(invoiceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionCheckoutResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMySubscriptionInvoiceCheckout(invoiceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.createMySubscriptionInvoiceCheckout']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a Mollie **first payment** (`sequenceType: first`) for the caller\'s own organisation and returns the hosted checkout to send the practice to. Completing it is what gives Scrivio a mandate to collect `AUTOMATED` payments under.  The payment is a **verification amount** — Mollie\'s minimum, €0,01. It pays for no period and is on no invoice; it exists to establish the mandate. It is asked for before the trial ends rather than at signup, so a payment page never stands in front of the free trial.  After the checkout — completed or abandoned — Mollie returns the practice to the app, which reads `GET /api/v1/subscription`: `mandateRequired` then says whether the mandate was given. That read also asks Mollie about a checkout that is still open, because Mollie\'s notification can arrive after the practice does.  Accepted only while `mandateRequired` would be `true`: an `ACTIVE` subscription whose `billingMode` is `AUTOMATED` and which has no valid mandate yet. Anything else is `409` — an `UNMANAGED` or overdue subscription, a practice paying by `INVOICE`, and one that has already signed. 
         * @summary Start the checkout that gives Scrivio a direct-debit mandate
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMySubscriptionMandateCheckout(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionCheckoutResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMySubscriptionMandateCheckout(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.createMySubscriptionMandateCheckout']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The same €0,01 first payment as `POST /api/v1/subscription/mandate-checkout`, on the same Mollie customer, for a practice that **already** has a valid mandate and wants to be collected from a different account — a new bank account, or a mandate set up wrongly.  **The current mandate stays in force until the new one is valid.** It is revoked — at Mollie and here, audited — only when the mandate this checkout produces arrives as valid, so there is never a moment without one: an invoice collected while the checkout is open is collected under the current mandate, and one collected afterwards under the new. A checkout that is abandoned or fails changes nothing.  Accepted only for an `ACTIVE` subscription whose `billingMode` is `AUTOMATED` and which has a valid mandate — exactly when `mandateDisplayLabel` is present on `GET /api/v1/subscription`. Without one, `POST /api/v1/subscription/mandate-checkout` is the step to take, and this is `409`. 
         * @summary Start the checkout that authorises a different account while the mandate stays in force
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMySubscriptionMandateReplacementCheckout(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionCheckoutResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMySubscriptionMandateReplacementCheckout(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.createMySubscriptionMandateReplacementCheckout']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The stored PDF, which is the one document of record: the invoice mail attaches the same object, so what the practice downloads is byte for byte what it is or was sent. It is rendered when the invoice is issued; an invoice whose render failed then is rendered and stored on its first download, and served from storage from then on.  `404` for an invoice of another organisation, and for one with no PDF — an invoice that covers no period, which `hasPdf` already reports as `false`. 
         * @summary The PDF of one invoice Scrivio issued the practice
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadMySubscriptionInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadMySubscriptionInvoicePdf(invoiceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.downloadMySubscriptionInvoicePdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the subscription of the caller\'s active organisation: where it stands, what it is on, what it pays per seat, how many seats it has today, and which discounts are running.  **There is no 404.** A practice that Scrivio invoices by hand is `UNMANAGED`, and that is an answer rather than an absence — it is the status every organisation that predates this context carries. An organisation whose subscription has not been recorded yet is reported as `UNMANAGED` for the same reason: it is invoiced outside Scrivio, which is exactly what the status means. The fields that only a managed subscription has — `planName`, `billingMode`, the current period — are then simply absent. 
         * @summary The caller\'s own subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMySubscription(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMySubscription(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.getMySubscription']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every invoice Scrivio has **issued** the caller\'s active organisation, newest first, credit notes included — and an invoice not mailed yet (`CONCEPT`) as well. A practice without a billing email is never mailed its invoices, and this list is then the only place it sees them.  An organisation Scrivio invoices by hand has no invoices here — those are sent outside Scrivio — so an `UNMANAGED` practice is answered with an empty list rather than a 404. 
         * @summary The invoices Scrivio issued the caller\'s own practice
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMySubscriptionInvoices(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<SubscriptionInvoiceResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMySubscriptionInvoices(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.listMySubscriptionInvoices']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records how the caller\'s own organisation wants Scrivio to collect: `INVOICE` (an emailed invoice, paid by bank transfer) or `AUTOMATED` (off-session direct debit or card against a Mollie mandate). Both share one ledger; only collection differs.  **`AUTOMATED` is stored at once and collected as `INVOICE` until a mandate exists.** Choosing it signs nothing: the mandate is given at Mollie\'s checkout, a separate step. The response\'s `collectedAs` says which of the two the next amount will actually be collected by, so the answer to this request already tells the practice that it is still waiting.  Refused with `409` for an `UNMANAGED` subscription — a practice Scrivio invoices by hand is not on a plan and has nothing to choose — and for a `CANCELED` one, which has left. Choosing the mode it already has changes nothing and is answered `200`. 
         * @summary Choose how the practice pays
         * @param {SubscriptionBillingModeRequest} subscriptionBillingModeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMySubscriptionBillingMode(subscriptionBillingModeRequest: SubscriptionBillingModeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMySubscriptionBillingMode(subscriptionBillingModeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.updateMySubscriptionBillingMode']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Withdraws a notice still running (#4902): the subscription carries on as before, billed as before, and `endsOn` is gone. Accepted up to and including `endsOn`; the day after, the subscription has ended. Accepted while the organisation is read-only, like the notice.  Refused with `409` when no notice runs. 
         * @summary Withdraw the notice the practice gave
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async withdrawMySubscriptionCancellation(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SubscriptionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.withdrawMySubscriptionCancellation(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SubscriptionsApi.withdrawMySubscriptionCancellation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SubscriptionsApi - factory interface
 */
export const SubscriptionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SubscriptionsApiFp(configuration)
    return {
        /**
         * Ends the subscription of the caller\'s own organisation on the terms its `cancellationOffer` states, which the screen shows before it asks (#4902):  - **During the free trial it ends now.** The subscription is `CANCELED`, every mandate is   withdrawn and the organisation becomes read-only. Nothing is ever invoiced: the free   period covers every day up to today and no later period is billed (agreement art. 4.2). - **Afterwards notice is given** (art. 9.1): one month, to the end of a calendar month.   The subscription stays `ACTIVE` and is billed through `endsOn`, and never after it. The   notice can be withdrawn until then with `DELETE`.  The practice is mailed a confirmation and Scrivio is told. Accepted while the organisation is read-only, for non-payment or a suspension alike: a practice that cannot write must still be able to leave.  Refused with `409` where there is no `cancellationOffer`: an `UNMANAGED` subscription, which Scrivio does not collect, one already `CANCELED`, and one with notice already running. 
         * @summary End the practice\'s own subscription (\"Abonnement opzeggen\")
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        cancelMySubscription(options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionResponse> {
            return localVarFp.cancelMySubscription(options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a Mollie **one-off** payment for the invoice\'s total and returns the hosted checkout to send the practice to — the \"Betalen\" link. Once Mollie reports it paid, the invoice is `BETAALD` through Mollie\'s notification, with nobody marking it by hand. A plain bank transfer remains possible and is still recorded by Scrivio.  The payment is not a direct debit: no mandate is given or used, and the invoice keeps `billingModeAtIssue` `INVOICE`. Every call creates a new payment, because a checkout URL is short-lived at Mollie; after it — paid or abandoned — Mollie returns the practice to the app, whose read of `GET /api/v1/subscription` asks Mollie about a checkout still open.  Accepted exactly where the invoice reads `payableOnline: true`, and refused with `409` while another payment of the invoice is pending or paid, so one invoice is not paid twice. 
         * @summary Start a Mollie checkout to pay one invoice the practice owes by transfer
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMySubscriptionInvoiceCheckout(invoiceId: string, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionCheckoutResponse> {
            return localVarFp.createMySubscriptionInvoiceCheckout(invoiceId, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a Mollie **first payment** (`sequenceType: first`) for the caller\'s own organisation and returns the hosted checkout to send the practice to. Completing it is what gives Scrivio a mandate to collect `AUTOMATED` payments under.  The payment is a **verification amount** — Mollie\'s minimum, €0,01. It pays for no period and is on no invoice; it exists to establish the mandate. It is asked for before the trial ends rather than at signup, so a payment page never stands in front of the free trial.  After the checkout — completed or abandoned — Mollie returns the practice to the app, which reads `GET /api/v1/subscription`: `mandateRequired` then says whether the mandate was given. That read also asks Mollie about a checkout that is still open, because Mollie\'s notification can arrive after the practice does.  Accepted only while `mandateRequired` would be `true`: an `ACTIVE` subscription whose `billingMode` is `AUTOMATED` and which has no valid mandate yet. Anything else is `409` — an `UNMANAGED` or overdue subscription, a practice paying by `INVOICE`, and one that has already signed. 
         * @summary Start the checkout that gives Scrivio a direct-debit mandate
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMySubscriptionMandateCheckout(options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionCheckoutResponse> {
            return localVarFp.createMySubscriptionMandateCheckout(options).then((request) => request(axios, basePath));
        },
        /**
         * The same €0,01 first payment as `POST /api/v1/subscription/mandate-checkout`, on the same Mollie customer, for a practice that **already** has a valid mandate and wants to be collected from a different account — a new bank account, or a mandate set up wrongly.  **The current mandate stays in force until the new one is valid.** It is revoked — at Mollie and here, audited — only when the mandate this checkout produces arrives as valid, so there is never a moment without one: an invoice collected while the checkout is open is collected under the current mandate, and one collected afterwards under the new. A checkout that is abandoned or fails changes nothing.  Accepted only for an `ACTIVE` subscription whose `billingMode` is `AUTOMATED` and which has a valid mandate — exactly when `mandateDisplayLabel` is present on `GET /api/v1/subscription`. Without one, `POST /api/v1/subscription/mandate-checkout` is the step to take, and this is `409`. 
         * @summary Start the checkout that authorises a different account while the mandate stays in force
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMySubscriptionMandateReplacementCheckout(options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionCheckoutResponse> {
            return localVarFp.createMySubscriptionMandateReplacementCheckout(options).then((request) => request(axios, basePath));
        },
        /**
         * The stored PDF, which is the one document of record: the invoice mail attaches the same object, so what the practice downloads is byte for byte what it is or was sent. It is rendered when the invoice is issued; an invoice whose render failed then is rendered and stored on its first download, and served from storage from then on.  `404` for an invoice of another organisation, and for one with no PDF — an invoice that covers no period, which `hasPdf` already reports as `false`. 
         * @summary The PDF of one invoice Scrivio issued the practice
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadMySubscriptionInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadMySubscriptionInvoicePdf(invoiceId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the subscription of the caller\'s active organisation: where it stands, what it is on, what it pays per seat, how many seats it has today, and which discounts are running.  **There is no 404.** A practice that Scrivio invoices by hand is `UNMANAGED`, and that is an answer rather than an absence — it is the status every organisation that predates this context carries. An organisation whose subscription has not been recorded yet is reported as `UNMANAGED` for the same reason: it is invoiced outside Scrivio, which is exactly what the status means. The fields that only a managed subscription has — `planName`, `billingMode`, the current period — are then simply absent. 
         * @summary The caller\'s own subscription
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMySubscription(options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionResponse> {
            return localVarFp.getMySubscription(options).then((request) => request(axios, basePath));
        },
        /**
         * Every invoice Scrivio has **issued** the caller\'s active organisation, newest first, credit notes included — and an invoice not mailed yet (`CONCEPT`) as well. A practice without a billing email is never mailed its invoices, and this list is then the only place it sees them.  An organisation Scrivio invoices by hand has no invoices here — those are sent outside Scrivio — so an `UNMANAGED` practice is answered with an empty list rather than a 404. 
         * @summary The invoices Scrivio issued the caller\'s own practice
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMySubscriptionInvoices(options?: RawAxiosRequestConfig): AxiosPromise<Array<SubscriptionInvoiceResponse>> {
            return localVarFp.listMySubscriptionInvoices(options).then((request) => request(axios, basePath));
        },
        /**
         * Records how the caller\'s own organisation wants Scrivio to collect: `INVOICE` (an emailed invoice, paid by bank transfer) or `AUTOMATED` (off-session direct debit or card against a Mollie mandate). Both share one ledger; only collection differs.  **`AUTOMATED` is stored at once and collected as `INVOICE` until a mandate exists.** Choosing it signs nothing: the mandate is given at Mollie\'s checkout, a separate step. The response\'s `collectedAs` says which of the two the next amount will actually be collected by, so the answer to this request already tells the practice that it is still waiting.  Refused with `409` for an `UNMANAGED` subscription — a practice Scrivio invoices by hand is not on a plan and has nothing to choose — and for a `CANCELED` one, which has left. Choosing the mode it already has changes nothing and is answered `200`. 
         * @summary Choose how the practice pays
         * @param {SubscriptionBillingModeRequest} subscriptionBillingModeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMySubscriptionBillingMode(subscriptionBillingModeRequest: SubscriptionBillingModeRequest, options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionResponse> {
            return localVarFp.updateMySubscriptionBillingMode(subscriptionBillingModeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Withdraws a notice still running (#4902): the subscription carries on as before, billed as before, and `endsOn` is gone. Accepted up to and including `endsOn`; the day after, the subscription has ended. Accepted while the organisation is read-only, like the notice.  Refused with `409` when no notice runs. 
         * @summary Withdraw the notice the practice gave
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        withdrawMySubscriptionCancellation(options?: RawAxiosRequestConfig): AxiosPromise<SubscriptionResponse> {
            return localVarFp.withdrawMySubscriptionCancellation(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SubscriptionsApi - object-oriented interface
 */
export class SubscriptionsApi extends BaseAPI {
    /**
     * Ends the subscription of the caller\'s own organisation on the terms its `cancellationOffer` states, which the screen shows before it asks (#4902):  - **During the free trial it ends now.** The subscription is `CANCELED`, every mandate is   withdrawn and the organisation becomes read-only. Nothing is ever invoiced: the free   period covers every day up to today and no later period is billed (agreement art. 4.2). - **Afterwards notice is given** (art. 9.1): one month, to the end of a calendar month.   The subscription stays `ACTIVE` and is billed through `endsOn`, and never after it. The   notice can be withdrawn until then with `DELETE`.  The practice is mailed a confirmation and Scrivio is told. Accepted while the organisation is read-only, for non-payment or a suspension alike: a practice that cannot write must still be able to leave.  Refused with `409` where there is no `cancellationOffer`: an `UNMANAGED` subscription, which Scrivio does not collect, one already `CANCELED`, and one with notice already running. 
     * @summary End the practice\'s own subscription (\"Abonnement opzeggen\")
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public cancelMySubscription(options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).cancelMySubscription(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a Mollie **one-off** payment for the invoice\'s total and returns the hosted checkout to send the practice to — the \"Betalen\" link. Once Mollie reports it paid, the invoice is `BETAALD` through Mollie\'s notification, with nobody marking it by hand. A plain bank transfer remains possible and is still recorded by Scrivio.  The payment is not a direct debit: no mandate is given or used, and the invoice keeps `billingModeAtIssue` `INVOICE`. Every call creates a new payment, because a checkout URL is short-lived at Mollie; after it — paid or abandoned — Mollie returns the practice to the app, whose read of `GET /api/v1/subscription` asks Mollie about a checkout still open.  Accepted exactly where the invoice reads `payableOnline: true`, and refused with `409` while another payment of the invoice is pending or paid, so one invoice is not paid twice. 
     * @summary Start a Mollie checkout to pay one invoice the practice owes by transfer
     * @param {string} invoiceId Invoice UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMySubscriptionInvoiceCheckout(invoiceId: string, options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).createMySubscriptionInvoiceCheckout(invoiceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a Mollie **first payment** (`sequenceType: first`) for the caller\'s own organisation and returns the hosted checkout to send the practice to. Completing it is what gives Scrivio a mandate to collect `AUTOMATED` payments under.  The payment is a **verification amount** — Mollie\'s minimum, €0,01. It pays for no period and is on no invoice; it exists to establish the mandate. It is asked for before the trial ends rather than at signup, so a payment page never stands in front of the free trial.  After the checkout — completed or abandoned — Mollie returns the practice to the app, which reads `GET /api/v1/subscription`: `mandateRequired` then says whether the mandate was given. That read also asks Mollie about a checkout that is still open, because Mollie\'s notification can arrive after the practice does.  Accepted only while `mandateRequired` would be `true`: an `ACTIVE` subscription whose `billingMode` is `AUTOMATED` and which has no valid mandate yet. Anything else is `409` — an `UNMANAGED` or overdue subscription, a practice paying by `INVOICE`, and one that has already signed. 
     * @summary Start the checkout that gives Scrivio a direct-debit mandate
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMySubscriptionMandateCheckout(options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).createMySubscriptionMandateCheckout(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The same €0,01 first payment as `POST /api/v1/subscription/mandate-checkout`, on the same Mollie customer, for a practice that **already** has a valid mandate and wants to be collected from a different account — a new bank account, or a mandate set up wrongly.  **The current mandate stays in force until the new one is valid.** It is revoked — at Mollie and here, audited — only when the mandate this checkout produces arrives as valid, so there is never a moment without one: an invoice collected while the checkout is open is collected under the current mandate, and one collected afterwards under the new. A checkout that is abandoned or fails changes nothing.  Accepted only for an `ACTIVE` subscription whose `billingMode` is `AUTOMATED` and which has a valid mandate — exactly when `mandateDisplayLabel` is present on `GET /api/v1/subscription`. Without one, `POST /api/v1/subscription/mandate-checkout` is the step to take, and this is `409`. 
     * @summary Start the checkout that authorises a different account while the mandate stays in force
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMySubscriptionMandateReplacementCheckout(options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).createMySubscriptionMandateReplacementCheckout(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The stored PDF, which is the one document of record: the invoice mail attaches the same object, so what the practice downloads is byte for byte what it is or was sent. It is rendered when the invoice is issued; an invoice whose render failed then is rendered and stored on its first download, and served from storage from then on.  `404` for an invoice of another organisation, and for one with no PDF — an invoice that covers no period, which `hasPdf` already reports as `false`. 
     * @summary The PDF of one invoice Scrivio issued the practice
     * @param {string} invoiceId Invoice UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadMySubscriptionInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).downloadMySubscriptionInvoicePdf(invoiceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the subscription of the caller\'s active organisation: where it stands, what it is on, what it pays per seat, how many seats it has today, and which discounts are running.  **There is no 404.** A practice that Scrivio invoices by hand is `UNMANAGED`, and that is an answer rather than an absence — it is the status every organisation that predates this context carries. An organisation whose subscription has not been recorded yet is reported as `UNMANAGED` for the same reason: it is invoiced outside Scrivio, which is exactly what the status means. The fields that only a managed subscription has — `planName`, `billingMode`, the current period — are then simply absent. 
     * @summary The caller\'s own subscription
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMySubscription(options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).getMySubscription(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every invoice Scrivio has **issued** the caller\'s active organisation, newest first, credit notes included — and an invoice not mailed yet (`CONCEPT`) as well. A practice without a billing email is never mailed its invoices, and this list is then the only place it sees them.  An organisation Scrivio invoices by hand has no invoices here — those are sent outside Scrivio — so an `UNMANAGED` practice is answered with an empty list rather than a 404. 
     * @summary The invoices Scrivio issued the caller\'s own practice
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMySubscriptionInvoices(options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).listMySubscriptionInvoices(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records how the caller\'s own organisation wants Scrivio to collect: `INVOICE` (an emailed invoice, paid by bank transfer) or `AUTOMATED` (off-session direct debit or card against a Mollie mandate). Both share one ledger; only collection differs.  **`AUTOMATED` is stored at once and collected as `INVOICE` until a mandate exists.** Choosing it signs nothing: the mandate is given at Mollie\'s checkout, a separate step. The response\'s `collectedAs` says which of the two the next amount will actually be collected by, so the answer to this request already tells the practice that it is still waiting.  Refused with `409` for an `UNMANAGED` subscription — a practice Scrivio invoices by hand is not on a plan and has nothing to choose — and for a `CANCELED` one, which has left. Choosing the mode it already has changes nothing and is answered `200`. 
     * @summary Choose how the practice pays
     * @param {SubscriptionBillingModeRequest} subscriptionBillingModeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMySubscriptionBillingMode(subscriptionBillingModeRequest: SubscriptionBillingModeRequest, options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).updateMySubscriptionBillingMode(subscriptionBillingModeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Withdraws a notice still running (#4902): the subscription carries on as before, billed as before, and `endsOn` is gone. Accepted up to and including `endsOn`; the day after, the subscription has ended. Accepted while the organisation is read-only, like the notice.  Refused with `409` when no notice runs. 
     * @summary Withdraw the notice the practice gave
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public withdrawMySubscriptionCancellation(options?: RawAxiosRequestConfig) {
        return SubscriptionsApiFp(this.configuration).withdrawMySubscriptionCancellation(options).then((request) => request(this.axios, this.basePath));
    }
}



